import { SEO } from "../components/SEO";
import { Reveal, Chapter, MaskLines } from "../components/Reveal";
import { IMAGES, CORE_VALUES, FIRM } from "../data/content";
import { Check } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us"
        description="Neeraj Battan & Associates is a Chartered Accountancy firm in Kurukshetra, Haryana, led by CA Neeraj Battan — providing taxation, audit, accounting, compliance and financial advisory services."
        path="/about"
      />

      <section className="relative bg-navy text-white overflow-hidden" data-testid="about-hero">
        <div className="grain-overlay" />
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-24">
          <p className="eyebrow mb-6">About the Firm</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1]" data-testid="about-heading">
            <MaskLines lines={["Integrity in Practice.",]} />
            <MaskLines lines={["Clarity in Advice."]} lineClassName="text-gold italic" baseDelay={0.3} />
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-background" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="01" label="Who We Are" />
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal className="order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-xl shadow-navy/10">
                <img src={IMAGES.meeting} alt="Professional advisory meeting" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="order-1 lg:order-2">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-tight">
                {FIRM.name}
              </h2>
              <p className="mt-2 text-sm font-mono uppercase tracking-[0.25em] text-gold">Chartered Accountants</p>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Neeraj Battan &amp; Associates is a Chartered Accountancy firm based in Kurukshetra, Haryana. Under the
                professional leadership of CA Neeraj Battan, the firm provides taxation, accounting, auditing,
                compliance and financial advisory services.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our approach is based on professional integrity, accuracy, confidentiality and a clear understanding of
                each client's requirements. We assist individuals, businesses, startups, firms, companies and other
                organisations in managing their financial, taxation and regulatory responsibilities.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-slate-100" data-testid="about-mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="02" label="Purpose & Direction" />
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-2xl bg-navy text-white p-10 relative overflow-hidden" data-testid="mission-card">
                <div className="grain-overlay" />
                <p className="eyebrow relative">Our Mission</p>
                <p className="relative mt-6 font-serif text-2xl leading-relaxed font-medium">
                  To provide dependable, ethical and practical professional services that help our clients remain
                  compliant and make informed financial decisions.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full rounded-2xl bg-white border border-slate-200 p-10" data-testid="vision-card">
                <p className="eyebrow">Our Vision</p>
                <p className="mt-6 font-serif text-2xl leading-relaxed font-medium text-navy">
                  To build long-term professional relationships by delivering accurate, transparent and client-focused
                  services.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-background" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="03" label="Core Values" />
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-12">
              The principles behind every engagement.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
            {CORE_VALUES.map((v, i) => (
              <Reveal key={v} delay={(i % 3) * 0.07}>
                <div className="flex items-center gap-4 py-5 border-b border-slate-200" data-testid={`core-value-${i}`}>
                  <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <Check size={16} className="text-gold shrink-0" />
                  <span className="font-serif text-xl font-medium text-navy">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-16">
            <div className="rounded-2xl overflow-hidden aspect-[21/9] shadow-xl shadow-navy/10 hidden sm:block">
              <img src={IMAGES.desk} alt="Executive workspace" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
